import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Phone, ArrowRight, Bot, BarChart3, Globe, Shield, Headphones, TrendingUp,
  ChevronDown, Star, Play, Sparkles, Mic, Cpu, Zap, Users, Lock, PlugZap,
  LineChart, MessageSquare, Clock, PhoneCall, Heart, Languages, ShoppingCart,
  Building2, GraduationCap, Stethoscope, Truck, Landmark, Clapperboard,
  Pause, Volume2, FileText, Scale, AlertTriangle, RefreshCw, Package, Menu, X as CloseIcon
} from 'lucide-react';

const LOGO_URL = 'https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png';

const HERO_BG_VIDEO = 'https://grocode.in/wp-content/uploads/2026/04/Free-Stock-Videos-motion-graphics-dots-waves-moving-randomly-on-grey-black-back.mp4';

const SITE_POLICIES = [
  { label: 'Privacy Policy', href: '/policies/privacy-policy', icon: Shield },
  { label: 'Terms & Conditions', href: '/policies/terms-conditions', icon: Scale },
  { label: 'Disclaimer', href: '/policies/disclaimer', icon: AlertTriangle },
  { label: 'Refund & Return Policy', href: '/policies/refund-policy', icon: RefreshCw },
  { label: 'Shipping Policy', href: '/policies/shipping-policy', icon: Package },
];

const FEATURES = [
  { icon: Heart, title: '100% Human Voice & Feel', desc: 'Our AI agents speak with natural human-like tone, emotions, and conversational flow. Customers can\'t tell the difference.' },
  { icon: Languages, title: '9 Indian Languages + Hinglish', desc: 'Hindi, English, Tamil, Gujarati, Bengali, Marathi, Telugu, Kannada, Malayalam — plus native Hinglish support for natural conversations.' },
  { icon: Clock, title: '24/7 Always-On Calling', desc: 'Your AI agents never sleep. Handle inbound and outbound calls around the clock without hiring night shifts.' },
  { icon: MessageSquare, title: 'Smart Escalation to Humans', desc: 'AI seamlessly transfers complex calls to human agents with full conversation context preserved.' },
  { icon: Cpu, title: 'Powered by DotCall Voice AI', desc: 'Enterprise-grade AI with the smartest voice technology available. Real-time speech-to-speech, zero latency.' },
  { icon: BarChart3, title: 'Deep Analytics & Transcripts', desc: 'Every call recorded, transcribed, and analyzed. Track sentiment, duration, outcomes, and agent performance.' },
];

const USE_CASES = [
  { icon: ShoppingCart, title: 'E-Commerce', desc: 'Abandoned cart recovery, order confirmations, delivery updates, and return processing — all automated.' },
  { icon: Landmark, title: 'Banking & Finance', desc: 'KYC verification, payment reminders, loan follow-ups, and account support with full compliance.' },
  { icon: Stethoscope, title: 'Healthcare', desc: 'Appointment scheduling, prescription reminders, patient follow-ups, and insurance claim assistance.' },
  { icon: Truck, title: 'Logistics', desc: 'Delivery confirmations, failed delivery recovery, driver coordination, and customer ETA updates.' },
  { icon: GraduationCap, title: 'Education', desc: 'Enrollment follow-ups, fee reminders, course information, and student support calls.' },
  { icon: Clapperboard, title: 'Entertainment & OTT', desc: 'Subscription renewals, plan upgrades, regional language support, and customer retention calls.' },
];

const WHY_DIFFERENT = [
  { icon: Mic, title: '100% Humanoid Voice', desc: 'Not robotic. Not scripted. Our AI speaks like a real Indian telecaller with natural pauses, emotions, and colloquialisms.' },
  { icon: Globe, title: 'Built for India', desc: 'Native support for Hindi, Hinglish, and 7 more Indian languages. Understands Indian accents, names, and cultural context.' },
  { icon: Zap, title: 'Sub-Second Latency', desc: 'Real-time voice AI with <1 second response time. No awkward silences or delays in conversation.' },
  { icon: PlugZap, title: 'CRM & E-Commerce Ready', desc: 'Direct integrations with HubSpot, Shopify, WooCommerce, Google Sheets, and Zapier. Trigger calls from any event.' },
  { icon: Shield, title: 'Enterprise Security', desc: 'End-to-end encryption, SOC 2 compliant, GDPR-ready. Your customer data stays protected.' },
  { icon: TrendingUp, title: 'Proven ROI', desc: 'Clients report 30% delivery recovery, 96% containment rate, and 34% support cost savings within 30 days.' },
  { icon: Users, title: 'Campaign Manager', desc: 'Upload CSV contacts, schedule campaigns, set retry logic, and run thousands of calls in parallel.' },
  { icon: LineChart, title: 'Knowledge Base RAG', desc: 'Upload your docs and FAQs. AI agents pull relevant answers in real-time using semantic search during calls.' },
  { icon: Lock, title: 'Wallet-Based Billing', desc: 'Transparent pay-as-you-go or included-minutes billing. See exactly what you spend per call with detailed breakdowns.' },
];

const FAQS = [
  { q: 'What is DotCall and how does it work?', a: 'DotCall is an AI voice agent platform that automates phone calls using advanced voice AI technology. It connects to Twilio for telephony and uses real-time speech-to-speech AI to have natural conversations in 9+ Indian languages including Hinglish.' },
  { q: 'How is DotCall different from other AI calling tools?', a: 'DotCall delivers 100% human-like voice quality — not robotic or scripted. We\'re built specifically for Indian businesses with native Hinglish support, sub-second latency, and deep integrations with Indian payment and CRM systems.' },
  { q: 'What languages does DotCall support?', a: 'Hindi (default), English, Tamil, Gujarati, Bengali, Marathi, Telugu, Kannada, and Malayalam. Plus natural Hinglish (Hindi-English mix) that mirrors how Indians actually speak.' },
  { q: 'How quickly can I set up my first AI agent?', a: 'Under 10 minutes. Our no-code builder lets you configure voice, language, personality, and conversation script. Upload your knowledge base and your agent is ready to call.' },
  { q: 'How does billing work?', a: 'We offer 3 plans: Standard (₹2,999/mo with 180 min included, then ₹8/min), Premium (₹7,999/mo with 650 min, then ₹6.5/min), and Enterprise (₹15,999/mo, pay as you go at ₹5.5/min). All paid plans use a wallet system for transparent billing.' },
  { q: 'Can DotCall integrate with my existing tools?', a: 'Yes! We integrate with HubSpot, Shopify, WooCommerce, Google Sheets, Zapier, and custom webhooks. Trigger AI calls on any event — abandoned carts, new orders, support tickets, and more.' },
  { q: 'Is my data secure?', a: 'Absolutely. End-to-end encryption for all calls, SOC 2 compliant, GDPR-ready. Call recordings and transcripts are stored securely and accessible only to your team.' },
];

const STATS = [
  { value: '100%', label: 'Human-Like Voice' },
  { value: '9+', label: 'Indian Languages' },
  { value: '<1s', label: 'Response Latency' },
  { value: '24/7', label: 'Always Available' },
];

function FAQ({ q, a, open, onClick, index }) {
  return (
    <div className="border-b border-white/[0.06]">
      <button onClick={onClick} className="w-full flex items-center justify-between py-5 text-left group" data-testid={`faq-item-${index}`}>
        <span className="text-white/80 font-medium text-sm md:text-base pr-6 group-hover:text-white transition-colors">{q}</span>
        <ChevronDown className={`w-4 h-4 text-amber-400/50 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180 text-amber-400' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 pb-5' : 'max-h-0'}`}>
        <p className="text-sm text-white/40 leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

function ShowcaseCard({ s }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = () => {
    if (!s.recording_url) { toast.error('No recording available'); return; }
    if (playing) { audioRef.current?.pause(); setPlaying(false); }
    else { audioRef.current?.play(); setPlaying(true); }
  };
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#0B1221] p-6 flex flex-col transition-all hover:border-amber-500/30 hover:shadow-[0_0_20px_rgba(245,158,11,0.1)]" data-testid={`showcase-card-${s.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="text-white font-semibold text-base mb-2">{s.title}</h4>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(s.tags || []).map((t, i) => (
              <Badge key={i} className="bg-white/5 text-slate-400 border-white/10 text-[10px] px-2 py-0.5">{t}</Badge>
            ))}
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">{s.description}</p>
        </div>
        <div className="flex flex-col gap-2 ml-4">
          <button onClick={toggle} className="w-10 h-10 rounded-xl bg-amber-600/20 hover:bg-amber-600/30 flex items-center justify-center transition-colors" data-testid={`play-${s.id}`}>
            {playing ? <Pause className="w-4 h-4 text-amber-300" /> : <Play className="w-4 h-4 text-amber-300 ml-0.5" />}
          </button>
          {s.recording_url && <button className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
            <Volume2 className="w-4 h-4 text-slate-400" />
          </button>}
        </div>
      </div>
      {s.phone_number && (
        <div className="mt-auto pt-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-2 justify-center py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <Phone className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 font-semibold text-lg tracking-wide">{s.phone_number}</span>
          </div>
        </div>
      )}
      {s.recording_url && <audio ref={audioRef} src={s.recording_url} onEnded={() => setPlaying(false)} />}
    </div>
  );
}

const LADY_IMG = 'https://i.ibb.co/vCs8FCY6/istockphoto-1798771892-612x612-removebg-preview.png';

const FLOATING_LEFT = [
  { icon: Headphones, label: 'Order Support Agent', color: 'bg-amber-600/80' },
  { icon: Clock, label: 'Appointment Assistant', color: 'bg-[#0B1221]' },
  { icon: ShoppingCart, label: 'Shopping Assistant', color: 'bg-[#0B1221]' },
  { icon: Shield, label: 'Insurance Help Agent', color: 'bg-[#0B1221]' },
];

const FLOATING_RIGHT = [
  { icon: Languages, label: 'Hindi + Hinglish Agent', color: 'bg-amber-600/80' },
  { icon: Globe, label: 'English Voice Agent', color: 'bg-[#0B1221]' },
];

const landingCSS = `
@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
@keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 0.5; } 50% { transform: scale(1.1); opacity: 0.2; } 100% { transform: scale(0.8); opacity: 0.5; } }
@keyframes slide-up { from { opacity:0; transform: translateY(30px); } to { opacity:1; transform: translateY(0); } }
@keyframes line-move { 0% { transform: translateY(100%); opacity: 0; } 50% { opacity: 0.3; } 100% { transform: translateY(-100%); opacity: 0; } }
@keyframes line-move-h { 0% { transform: translateX(-100%); opacity: 0; } 50% { opacity: 0.2; } 100% { transform: translateX(100%); opacity: 0; } }
.float-anim { animation: float 4s ease-in-out infinite; }
.pulse-ring { animation: pulse-ring 4s ease-in-out infinite; }
.slide-up { animation: slide-up 0.7s ease-out forwards; }
.anim-line { position: absolute; background: linear-gradient(to bottom, transparent, rgba(245,158,11,0.15), transparent); width: 1px; height: 200px; animation: line-move 6s linear infinite; }
.anim-line-h { position: absolute; background: linear-gradient(to right, transparent, rgba(245,158,11,0.1), transparent); height: 1px; width: 300px; animation: line-move-h 8s linear infinite; }

/* Soft fade at bottom of lady image so sharp cut blends into the dark background */
.lady-image {
  -webkit-mask-image: linear-gradient(to bottom, black 0%, black 72%, rgba(0,0,0,0.65) 85%, rgba(0,0,0,0.15) 95%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 0%, black 72%, rgba(0,0,0,0.65) 85%, rgba(0,0,0,0.15) 95%, transparent 100%);
}
.lady-image-wrap { position: relative; }
.lady-image-wrap::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -6px;
  transform: translateX(-50%);
  width: 60%;
  height: 35px;
  background: radial-gradient(ellipse at center, rgba(245,158,11,0.18) 0%, rgba(245,158,11,0.05) 45%, transparent 75%);
  filter: blur(14px);
  pointer-events: none;
  z-index: 1;
}
`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callStarted, setCallStarted] = useState(false);
  const [callPhase, setCallPhase] = useState('idle'); // 'idle' | 'preparing' | 'calling'
  const [openFaq, setOpenFaq] = useState(0);
  const [showcases, setShowcases] = useState([]);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const demoSectionRef = useRef(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API}/api/demo-showcases`).then(r => setShowcases(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const closeOnClickAway = () => setPolicyOpen(false);
    if (policyOpen) {
      document.addEventListener('click', closeOnClickAway);
      return () => document.removeEventListener('click', closeOnClickAway);
    }
  }, [policyOpen]);

  const handlePhoneChange = (e) => {
    // Only digits, max 10, +91 is a fixed prefix shown separately
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(digits);
  };

  const handleTrialCall = async () => {
    if (phoneNumber.length < 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    setCallStarted(true);
    setCallPhase('preparing');
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API}/api/voice/demo-call`, { phone_number: `+91${phoneNumber}` }, { timeout: 20000 });
      setCallPhase('calling');
      toast.success('Demo call initiated! You will receive a call shortly.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to make demo call';
      toast.error(detail, { duration: err.response?.status === 429 ? 5000 : 3000 });
      setCallPhase('idle');
    }
    setTimeout(() => { setCallStarted(false); setCallPhase('idle'); }, 6000);
  };

  const scrollToDemoSection = () => {
    demoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const anim = () => 'animate-fade-in';

  return (
    <div className="min-h-screen bg-[#020617] text-white overflow-x-hidden" data-testid="landing-page">
      <style>{landingCSS}</style>

      {/* ===== TOPBAR ===== */}
      <div className="bg-amber-600 text-white text-center py-2 px-4 text-sm relative z-[60]" data-testid="topbar">
        <span className="font-medium">New: AI Voice Agents Now Support 9 Indian Languages + Hinglish</span>
        <span className="mx-2 text-white/60">|</span>
        <a href="/pricing" className="underline font-semibold hover:text-white/90">Try Free for 10 Minutes</a>
      </div>

      {/* ===== NAV (Rounded) ===== */}
      <nav className="sticky top-0 z-50 px-4 py-3" data-testid="landing-nav">
        <div className="max-w-6xl mx-auto bg-[#0B1221]/90 backdrop-blur-2xl border border-white/[0.08] rounded-2xl px-6 h-14 flex items-center justify-between shadow-xl">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="DotCall" className="h-7 w-auto" data-testid="logo" />
          </div>
          <div className="hidden md:flex items-center gap-7 text-sm">
            <a href="/pricing" className="text-white/50 hover:text-white transition-colors">Pricing</a>
            <a href="/blog" className="text-white/50 hover:text-white transition-colors" data-testid="nav-blog">Blog</a>
            <a href="/contact" className="text-white/50 hover:text-white transition-colors" data-testid="nav-contact-us">Contact Us</a>
            {/* Site Policy dropdown */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setPolicyOpen(!policyOpen)}
                className="flex items-center gap-1 text-white/50 hover:text-white transition-colors focus:outline-none"
                data-testid="nav-site-policy"
              >
                Site Policy
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${policyOpen ? 'rotate-180' : ''}`} />
              </button>
              {policyOpen && (
                <div className="absolute top-full right-0 mt-3 w-64 rounded-xl border border-white/[0.08] bg-[#0B1221] shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-1" data-testid="site-policy-dropdown">
                  {SITE_POLICIES.map((p) => (
                    <a
                      key={p.href}
                      href={p.href}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] last:border-0"
                      data-testid={`policy-link-${p.label.toLowerCase().replace(/\s|&/g, '-')}`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-amber-600/15 flex items-center justify-center flex-shrink-0">
                        <p.icon className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                      <span>{p.label}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-white/50 hover:text-white text-sm hidden md:inline-flex" data-testid="nav-login-button">Sign In</Button>
            <Button onClick={() => navigate('/pricing')} className="bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-full px-5 font-semibold shadow-[0_0_20px_rgba(245,158,11,0.3)]" data-testid="nav-signup-button">Get Started Free</Button>
            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white/70 hover:text-white"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <CloseIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden max-w-6xl mx-auto mt-2 rounded-2xl border border-white/[0.08] bg-[#0B1221]/95 backdrop-blur-2xl p-4 shadow-xl" data-testid="mobile-menu">
            <a href="/pricing" className="block px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors">Pricing</a>
            <a href="/blog" className="block px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors">Blog</a>
            <a href="/contact" className="block px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors">Contact Us</a>
            <button type="button" onClick={() => navigate('/login')} className="w-full text-left px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors">Sign In</button>
            <div className="border-t border-white/[0.06] my-2" />
            <div className="text-[10px] uppercase tracking-wider text-white/30 px-3 pt-1 pb-1.5">Site Policy</div>
            {SITE_POLICIES.map((p) => (
              <a key={p.href} href={p.href} className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                <p.icon className="w-4 h-4 text-amber-400/80" />
                <span>{p.label}</span>
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative pt-10 pb-8 lg:pt-14 lg:pb-12 px-6 overflow-hidden" data-testid="hero-section">
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          src={HERO_BG_VIDEO}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          data-testid="hero-bg-video"
          aria-hidden="true"
        />
        {/* Dark overlay on top of video for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/65 via-[#020617]/55 to-[#020617]/80 pointer-events-none" />

        {/* Animated background lines + amber glow (on top of video overlay) */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="anim-line" style={{left: '10%', animationDelay: '0s'}} />
          <div className="anim-line" style={{left: '25%', animationDelay: '1.5s'}} />
          <div className="anim-line" style={{left: '50%', animationDelay: '3s'}} />
          <div className="anim-line" style={{left: '75%', animationDelay: '0.8s'}} />
          <div className="anim-line" style={{left: '90%', animationDelay: '2.5s'}} />
          <div className="anim-line-h" style={{top: '20%', animationDelay: '1s'}} />
          <div className="anim-line-h" style={{top: '60%', animationDelay: '3.5s'}} />
          <div className="anim-line-h" style={{top: '80%', animationDelay: '0.5s'}} />
          <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-amber-600/[0.04] rounded-full blur-[120px]" />
        </div>

        <div className={`max-w-5xl mx-auto relative z-10 text-center ${anim()}`}>
          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] text-white mb-4 leading-[1.1]" data-testid="hero-title">
            AI Voice Agents That Sound<br />
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Completely Human</span>
          </h1>
          <p className="text-base lg:text-lg text-white/60 mb-8 max-w-2xl mx-auto leading-relaxed">
            Automate sales, support, and outreach calls in Hindi, English, Hinglish & 7 more Indian languages. Your customers won't know it's AI.
          </p>

          {/* CTA Demo Call */}
          <div ref={demoSectionRef} className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-2" data-testid="demo-call-section">
            {/* Phone input with left-aligned +91 prefix */}
            <div className="relative sm:w-64 w-full max-w-xs">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none z-10">
                <span className="text-white/70 text-base font-medium">+91</span>
                <span className="h-5 w-px bg-white/15" />
              </div>
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={phoneNumber}
                onChange={handlePhoneChange}
                className="bg-white/5 border-white/10 text-white h-12 text-base pl-[72px] pr-4 rounded-full placeholder:text-white/25 text-left w-full"
                placeholder="98765 43210"
                data-testid="demo-phone-input"
              />
            </div>
            <Button
              onClick={handleTrialCall}
              disabled={callStarted}
              data-testid="demo-call-button"
              className="h-12 px-8 bg-amber-600 hover:bg-amber-500 text-white rounded-full font-bold shadow-[0_0_30px_rgba(245,158,11,0.35)] text-base"
            >
              {callPhase === 'preparing' ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
                  Preparing your demo...
                </span>
              ) : callPhase === 'calling' ? (
                <span className="flex items-center gap-2"><Phone className="w-4 h-4 animate-pulse" /> Calling...</span>
              ) : (
                <span className="flex items-center gap-2"><PhoneCall className="w-4 h-4" /> Try a Demo Call</span>
              )}
            </Button>
          </div>
          <p className="text-[11px] text-white/40 mb-10 font-light">Free 2-minute demo call. No signup required. One call per number.</p>

          {/* Lady Image + Floating Cards */}
          <div className="relative mx-auto max-w-4xl min-h-[440px] sm:min-h-[500px] lg:min-h-[460px]">
            {/* Pulse rings behind lady */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] rounded-full border border-amber-500/10 pulse-ring" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[500px] sm:h-[500px] rounded-full border border-amber-500/[0.06] pulse-ring" style={{animationDelay: '1s'}} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] sm:w-[650px] sm:h-[650px] rounded-full border border-amber-500/[0.03] pulse-ring" style={{animationDelay: '2s'}} />

            {/* Floating cards LEFT (desktop) */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-3 w-56 z-10">
              {FLOATING_LEFT.map((f, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-white/[0.08] shadow-lg float-anim ${f.color}`} style={{animationDelay: `${i * 0.6}s`}}>
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0"><f.icon className="w-4 h-4 text-amber-300" /></div>
                  <span className="text-sm font-medium text-white/90">{f.label}</span>
                </div>
              ))}
            </div>

            {/* Floating cards RIGHT (desktop) */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-3 w-52 z-10">
              {FLOATING_RIGHT.map((f, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-white/[0.08] shadow-lg float-anim ${f.color}`} style={{animationDelay: `${i * 0.8 + 0.3}s`}}>
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0"><f.icon className="w-4 h-4 text-amber-300" /></div>
                  <span className="text-sm font-medium text-white/90">{f.label}</span>
                </div>
              ))}
            </div>

            {/* Floating cards MOBILE+TABLET (absolute mini chips around lady) */}
            <div className="lg:hidden absolute inset-0 z-10 pointer-events-none">
              {/* LEFT column */}
              <div className="absolute left-1 sm:left-4 top-[12%] flex flex-col gap-2 sm:gap-3">
                {FLOATING_LEFT.map((f, i) => (
                  <div
                    key={`ml-${i}`}
                    className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-white/[0.1] shadow-lg float-anim ${f.color} backdrop-blur-sm`}
                    style={{ animationDelay: `${i * 0.6}s`, maxWidth: '135px' }}
                  >
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-300" />
                    </div>
                    <span className="text-[9px] sm:text-[11px] font-medium text-white/90 leading-tight">{f.label}</span>
                  </div>
                ))}
              </div>
              {/* RIGHT column */}
              <div className="absolute right-1 sm:right-4 top-[22%] flex flex-col gap-2 sm:gap-3">
                {FLOATING_RIGHT.map((f, i) => (
                  <div
                    key={`mr-${i}`}
                    className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-white/[0.1] shadow-lg float-anim ${f.color} backdrop-blur-sm`}
                    style={{ animationDelay: `${i * 0.8 + 0.3}s`, maxWidth: '135px' }}
                  >
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-300" />
                    </div>
                    <span className="text-[9px] sm:text-[11px] font-medium text-white/90 leading-tight">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Lady Image (centered) with soft bottom fade */}
            <div className="relative z-20 flex justify-center lady-image-wrap">
              <img src={LADY_IMG} alt="AI Voice Agent" className="lady-image h-[380px] lg:h-[440px] object-contain drop-shadow-2xl" data-testid="hero-image" />
            </div>

            {/* Try a Call button below lady */}
            <div className="flex justify-center -mt-2 relative z-20">
              <button onClick={scrollToDemoSection} disabled={callStarted}
                className="flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-full font-semibold shadow-[0_0_25px_rgba(245,158,11,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-testid="try-call-bottom">
                <PhoneCall className="w-5 h-5" /> Try a Call
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section className="py-12 px-6 border-y border-white/[0.04] bg-white/[0.01]">
        <div className={`max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 ${anim()}`}>
          {STATS.map((s, i) => (
            <div key={i} className="text-center" style={{animationDelay: `${i * 100}ms`}}>
              <div className="text-3xl md:text-4xl font-bold font-['Outfit'] bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent mb-1">{s.value}</div>
              <div className="text-sm text-white/30">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== DEMO SHOWCASES (moved up to 2nd fold, right after Stats bar) ===== */}
      {showcases.length > 0 && (
        <section id="demo-showcases" className="py-20 px-6">
          <div className={`max-w-5xl mx-auto ${anim()}`}>
            <div className="text-center mb-14">
              <Badge className="bg-cyan-600/15 text-cyan-300 border-cyan-500/30 mb-4 text-xs px-3 py-1">Hear It Live</Badge>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3">
                Listen to Our<br /><span className="bg-gradient-to-r from-cyan-400 to-amber-400 bg-clip-text text-transparent">AI Agents in Action</span>
              </h2>
              <p className="text-base text-white/45 max-w-xl mx-auto">Real call recordings from our AI agents. Play them, or call the number yourself to experience it live.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="showcases-grid">
              {showcases.map(s => <ShowcaseCard key={s.id} s={s} />)}
            </div>
          </div>
        </section>
      )}

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-20 px-6">
        <div className={`max-w-6xl mx-auto ${anim()}`}>
          <div className="text-center mb-14">
            <Badge className="bg-amber-600/15 text-amber-300 border-amber-500/30 mb-4 text-xs px-3 py-1">Built for Real Business</Badge>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3">
              AI Voice Agents That<br /><span className="bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent">Actually Deliver Results</span>
            </h2>
            <p className="text-base text-white/45 max-w-2xl mx-auto">Everything you need to automate voice interactions, delight customers, and scale your operations.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div key={i} className="group rounded-2xl border border-white/[0.06] bg-[#0B1221]/60 p-6 hover:border-amber-500/20 hover:shadow-[0_0_30px_rgba(245,158,11,0.06)] transition-all duration-300" style={{animationDelay: `${i * 80}ms`}}>
                <div className="w-11 h-11 rounded-xl bg-amber-600/15 flex items-center justify-center mb-4 group-hover:bg-amber-600/25 transition-colors">
                  <f.icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{f.title}</h3>
                <p className="text-sm text-white/45 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY DIFFERENT ===== */}
      <section id="why-different" className="py-20 px-6 bg-white/[0.01]">
        <div className={`max-w-6xl mx-auto ${anim()}`}>
          <div className="text-center mb-14">
            <Badge className="bg-amber-600/15 text-amber-300 border-amber-500/30 mb-4 text-xs px-3 py-1">Why We're Different</Badge>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3">
              Not Just Another AI.<br /><span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Built Different for India.</span>
            </h2>
            <p className="text-base text-white/45 max-w-2xl mx-auto">Other AI callers sound robotic. DotCall agents speak like real Indian telecallers — with emotion, context, and cultural understanding.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_DIFFERENT.map((w, i) => (
              <div key={i} className="rounded-2xl border border-white/[0.06] bg-[#0B1221]/60 p-6 hover:border-amber-500/20 transition-all duration-300" style={{animationDelay: `${i * 60}ms`}}>
                <div className="w-10 h-10 rounded-lg bg-amber-600/15 flex items-center justify-center mb-3">
                  <w.icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{w.title}</h3>
                <p className="text-xs text-white/45 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DEMO SHOWCASES (moved to 2nd fold above) ===== */}

      {/* ===== USE CASES ===== */}
      <section id="use-cases" className="py-20 px-6 bg-white/[0.01]">
        <div className={`max-w-6xl mx-auto ${anim()}`}>
          <div className="text-center mb-14">
            <Badge className="bg-emerald-600/15 text-emerald-300 border-emerald-500/30 mb-4 text-xs px-3 py-1">Built for Every Industry</Badge>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3">
              Smart Solutions for<br /><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Every Use Case</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {USE_CASES.map((u, i) => (
              <div key={i} className="rounded-2xl border border-white/[0.06] bg-[#0B1221]/60 p-6 hover:border-emerald-500/20 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-emerald-600/15 flex items-center justify-center mb-3">
                  <u.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{u.title}</h3>
                <p className="text-sm text-white/45 leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-600/[0.06] rounded-full blur-[120px]" />
        </div>
        <div className={`max-w-3xl mx-auto text-center relative z-10 ${anim()}`}>
          <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">
            Ready to Transform Your<br /><span className="bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent">Customer Calls?</span>
          </h2>
          <p className="text-base text-white/45 mb-8 max-w-lg mx-auto">Start free with 10 minutes. No credit card required. Deploy your first AI voice agent in under 10 minutes.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button onClick={() => navigate('/pricing')} data-testid="cta-signup-button"
              className="h-12 px-8 bg-amber-600 hover:bg-amber-500 text-white text-base font-bold shadow-[0_0_30px_rgba(245,158,11,0.3)] rounded-full">
              Start Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/pricing')} data-testid="cta-pricing-button"
              className="h-12 px-8 bg-[#0B1221] hover:bg-[#162032] text-white text-base rounded-full border border-white/[0.08]">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 px-6 bg-white/[0.01]" id="faq">
        <div className={`max-w-2xl mx-auto ${anim()}`}>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-3">
              Frequently Asked<br /><span className="bg-gradient-to-r from-amber-400 to-cyan-400 bg-clip-text text-transparent">Questions</span>
            </h2>
          </div>
          <div className="bg-[#0B1221] border border-white/[0.06] rounded-2xl px-6 md:px-8" data-testid="faq-section">
            {FAQS.map((faq, i) => (
              <FAQ key={i} q={faq.q} a={faq.a} open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-white/[0.08] bg-gradient-to-b from-[#0B1221]/60 to-[#020617] relative overflow-hidden" data-testid="landing-footer">
        {/* Ambient glow effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[600px] h-[300px] bg-amber-600/[0.03] rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/3 w-[500px] h-[250px] bg-cyan-600/[0.02] rounded-full blur-[90px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
            {/* Brand Section */}
            <div className="md:col-span-5">
              <div className="flex items-center gap-2 mb-5">
                <img src={LOGO_URL} alt="DotCall" className="h-8 w-auto" />
              </div>
              <p className="text-sm text-white/40 leading-relaxed max-w-sm mb-6">
                AI-powered voice agents for Indian enterprises. 100% human-like conversations in Hindi, Hinglish, and 7 more Indian languages. Transform your customer calls with AI.
              </p>
              <div className="flex items-center gap-3">
                <a href="#" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all hover:border-amber-500/30">
                  <svg className="w-4 h-4 text-white/50" fill="currentColor" viewBox="0 0 24 24"><path d="M22.46 6c-.85.38-1.78.64-2.75.76 1-.6 1.76-1.55 2.12-2.68-.93.55-1.96.95-3.06 1.17-.88-.94-2.13-1.53-3.52-1.53-2.66 0-4.82 2.16-4.82 4.82 0 .38.04.75.13 1.1-4-.2-7.55-2.12-9.93-5.04-.42.72-.66 1.55-.66 2.44 0 1.67.85 3.15 2.15 4.02-.79-.03-1.53-.24-2.18-.6v.06c0 2.34 1.66 4.29 3.87 4.73-.4.11-.83.17-1.27.17-.31 0-.62-.03-.92-.08.62 1.94 2.43 3.35 4.58 3.39-1.68 1.32-3.8 2.1-6.1 2.1-.4 0-.79-.02-1.17-.07 2.18 1.4 4.77 2.21 7.55 2.21 9.05 0 14-7.5 14-14 0-.21 0-.42-.02-.63.96-.69 1.8-1.56 2.46-2.55z"/></svg>
                </a>
                <a href="#" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all hover:border-amber-500/30">
                  <svg className="w-4 h-4 text-white/50" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.27c-.97 0-1.75-.79-1.75-1.76s.78-1.77 1.75-1.77 1.75.79 1.75 1.77-.79 1.76-1.75 1.76zm13.5 11.27h-3v-5.6c0-3.37-4-3.12-4 0v5.6h-3v-10h3v1.77c1.4-2.59 7-2.78 7 2.48v5.75z"/></svg>
                </a>
                <a href="#" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all hover:border-amber-500/30">
                  <svg className="w-4 h-4 text-white/50" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.97.24 2.43.41.61.24 1.04.52 1.5.98.46.46.74.89.98 1.5.17.46.36 1.26.41 2.43.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.24 1.97-.41 2.43-.24.61-.52 1.04-.98 1.5-.46.46-.89.74-1.5.98-.46.17-1.26.36-2.43.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.97-.24-2.43-.41-.61-.24-1.04-.52-1.5-.98-.46-.46-.74-.89-.98-1.5-.17-.46-.36-1.26-.41-2.43-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.24-1.97.41-2.43.24-.61.52-1.04.98-1.5.46-.46.89-.74 1.5-.98.46-.17 1.26-.36 2.43-.41 1.27-.06 1.65-.07 4.85-.07m0-2.16c-3.26 0-3.67.01-4.95.07-1.28.06-2.16.27-2.93.57-.79.31-1.46.72-2.13 1.39-.67.67-1.08 1.34-1.39 2.13-.3.77-.51 1.65-.57 2.93-.06 1.28-.07 1.69-.07 4.95s.01 3.67.07 4.95c.06 1.28.27 2.16.57 2.93.31.79.72 1.46 1.39 2.13.67.67 1.34 1.08 2.13 1.39.77.3 1.65.51 2.93.57 1.28.06 1.69.07 4.95.07s3.67-.01 4.95-.07c1.28-.06 2.16-.27 2.93-.57.79-.31 1.46-.72 2.13-1.39.67-.67 1.08-1.34 1.39-2.13.3-.77.51-1.65.57-2.93.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.28-.27-2.16-.57-2.93-.31-.79-.72-1.46-1.39-2.13-.67-.67-1.34-1.08-2.13-1.39-.77-.3-1.65-.51-2.93-.57-1.28-.06-1.69-.07-4.95-.07z"/><circle cx="12" cy="12" r="3.6"/><circle cx="18.4" cy="5.6" r="1.4"/></svg>
                </a>
              </div>
            </div>
            
            {/* Links Sections */}
            <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-10">
              <div>
                <h4 className="text-sm font-semibold text-white/70 mb-5 uppercase tracking-wider">Product</h4>
                <ul className="space-y-3">
                  {['AI Agents', 'Campaign Manager', 'Knowledge Base', 'Integrations', 'Phone Numbers'].map(item => (
                    <li key={item}><a href="#" className="text-sm text-white/35 hover:text-white/70 transition-colors inline-flex items-center gap-1.5 group">
                      <span>{item}</span>
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a></li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold text-white/70 mb-5 uppercase tracking-wider">Company</h4>
                <ul className="space-y-3">
                  {[
                    {label: 'Pricing', href: '/pricing'},
                    {label: 'Blog', href: '/blog'},
                    {label: 'Contact Us', href: '/contact'},
                    {label: 'About Us', href: '#'},
                    {label: 'Careers', href: '#'},
                  ].map(item => (
                    <li key={item.label}><a href={item.href} className="text-sm text-white/35 hover:text-white/70 transition-colors inline-flex items-center gap-1.5 group">
                      <span>{item.label}</span>
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a></li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold text-white/70 mb-5 uppercase tracking-wider">Legal</h4>
                <ul className="space-y-3">
                  {[
                    {label: 'Privacy Policy', href: '/policies/privacy-policy'},
                    {label: 'Terms', href: '/policies/terms-conditions'},
                    {label: 'Disclaimer', href: '/policies/disclaimer'},
                    {label: 'Refund Policy', href: '/policies/refund-policy'},
                    {label: 'Shipping', href: '/policies/shipping-policy'},
                  ].map(item => (
                    <li key={item.label}><a href={item.href} className="text-sm text-white/35 hover:text-white/70 transition-colors inline-flex items-center gap-1.5 group">
                      <span>{item.label}</span>
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-white/[0.06] pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-white/25" data-testid="footer-copyright">
              © {new Date().getFullYear()} DotCall. All Rights Reserved.
            </p>
            <div className="flex items-center gap-6 text-xs text-white/20">
              <span>Made with ❤️ in India</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ===== MADE FOR INDIANS BADGE (bottom-left, fixed, hidden on mobile) ===== */}
      <div className="hidden lg:block fixed bottom-6 left-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-700" data-testid="india-badge">
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-black backdrop-blur-md shadow-xl border border-[#d97834]/50 hover:scale-105 transition-transform cursor-pointer">
          <span className="text-sm font-semibold text-white">
            Made For Indians
          </span>
          <Heart className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" />
        </div>
      </div>
    </div>
  );
}
